import { KeyboardEvent, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import Modal, { ModalButton, ModalSecondaryButton } from "../../../../../../components/Modal";
import { canMoveSection, getPages } from "../../../../../../model/pages";
import { availableSections } from "../../../../../../model/sectionDefinitions";
import { Section, Site } from "../../../../../../model/site";
import { classNames } from "../../../../../../utils/styles";
import { KeepoList } from "../../../../../Domains/styles/connect";

/* a page that can't take the section looks off (it isn't `disabled`: it stays reachable to say why) */
const PageList = styled(KeepoList)`
  .keepo.blocked {
    cursor: default;
    opacity: 0.45;

    &:hover {
      border-color: var(--color-neutral-base);
    }
  }
`;

type Props = {
  site: Site;
  section: Section;
  /** The page the section is on (null: the home): not offered. */
  from: string | null;
  onMove: (to: string | null) => void;
  onClose: () => void;
};

/**
 * Which page a section goes to: the Keepo's other pages (the home among them) to pick from, as the Keepos on
 * /domains; one that can't take it (it already has that one-of-a-kind section) is there but can't be picked (it is
 * announced as unavailable, with the reason, and stays reachable by the keyboard). One tab stop for the group, the
 * arrows move through it. Cancel and Send at the bottom.
 */
export const MoveSectionModal = ({ site, section, from, onMove, onClose }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.editor.sections.move";
  const [selected, setSelected] = useState<string | null | undefined>(undefined);

  const isMultiple = !!availableSections.find(
    ({ type, subType }) => type === section.type && subType === section.subType
  )?.multiple;
  const options = [
    { id: null, title: t("pages.editor.pages.home"), address: "/" },
    ...getPages(site).map(({ id, title, slug }) => ({ id, title, address: `/${slug}` })),
  ]
    .filter(({ id }) => id !== from)
    .map(option => ({ ...option, blocked: canMoveSection(site, section, option.id, isMultiple) !== "ok" }));

  // one tab stop (roving): the one picked, or else the first that can be picked
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const baseId = useId();
  const [focused, setFocused] = useState<number>();
  const firstFree = options.findIndex(({ blocked }) => !blocked);
  const selectedAt = options.findIndex(({ id }) => id === selected);
  const tabStop = focused ?? (selectedAt >= 0 ? selectedAt : Math.max(firstFree, 0));

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = options.length - 1;
    const to = {
      ArrowDown: tabStop === last ? 0 : tabStop + 1,
      ArrowRight: tabStop === last ? 0 : tabStop + 1,
      ArrowUp: tabStop === 0 ? last : tabStop - 1,
      ArrowLeft: tabStop === 0 ? last : tabStop - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (to !== undefined && options.length) {
      event.preventDefault();
      setFocused(to);
      buttons.current[to]?.focus();
    }
  };

  return (
    <Modal
      outsideExit
      onExit={onClose}
      title={t(`${prefix}.title`)}
      subtitle={t(`${prefix}.subtitle`)}
      exitLabel={t(`${prefix}.cancel`)}
      footer={
        <>
          <ModalSecondaryButton type="button" onClick={onClose}>
            {t(`${prefix}.cancel`)}
          </ModalSecondaryButton>
          <ModalButton disabled={selected === undefined} onClick={() => selected !== undefined && onMove(selected)}>
            {t(`${prefix}.send`)}
          </ModalButton>
        </>
      }>
      <PageList>
        {/* the group owns the radios through the list, which is only layout */}
        <div role="radiogroup" aria-label={t(`${prefix}.title`)} onKeyDown={onKeyDown}>
          <ul role="none">
            {options.map(({ id, title, address, blocked }, index) => {
              const isSelected = selected === id;
              const titleId = `${baseId}-title-${index}`;
              const infoId = `${baseId}-info-${index}`;
              return (
                <li key={id ?? "home"} role="none">
                  <button
                    ref={element => {
                      buttons.current[index] = element;
                    }}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    // not `disabled`: it stays reachable, and says why it can't be picked
                    aria-disabled={blocked || undefined}
                    aria-labelledby={titleId}
                    aria-describedby={infoId}
                    tabIndex={index === tabStop ? 0 : -1}
                    className={classNames("keepo", { selected: isSelected, blocked })}
                    onFocus={() => setFocused(index)}
                    onClick={() => !blocked && setSelected(id)}>
                    <span className="avatar" aria-hidden="true">
                      {title.trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="text">
                      <b id={titleId}>{title}</b>
                      <span id={infoId}>{blocked ? t(`${prefix}.already`) : address}</span>
                    </span>
                    <span className="radio" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </PageList>
    </Modal>
  );
};
